import AsyncWrapper from '@/components/AsyncWrapper';
import { CommandEntityReady, CommandEntityTypeEnum } from '@/components/Commander';
import { message } from '@/components/Message';
import Modal from '@/components/Modal';
import { emitGlobalEvent } from '@/components/QRScanner/GlobalScanner';
import { Page } from '@/models/common';
import { User, UserEndpoint, UserFullInfo, useUserFullInfo } from '@/models/user';
import { IS_POS } from '@/utils/env';
import { playSound } from '@/utils/sound';
import { CloseCircleOutlined } from '@ant-design/icons';
import { createStyles } from 'antd-style';
import React, { useEffect, useState } from 'react';
import { useRequest } from '../../api/hooks';
import MultiUserSelector from './multiUserSelector';
import UserInfo from './userInfo';

const useStyles = createStyles(({ css }) => {
  return {
    modal: css`
      .ant-modal-content {
        box-shadow: none;
      }
    `,
    wrapper: css`
      position: relative;
      user-select: none;
    `,
    actions: css`
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      top: -40px;
      width: 100%;
    `,
    actionItem: css`
      font-size: 32px;
      color: #ffffff;
    `,
  };
});

export type UserModalProps = {
  open?: boolean;
  onClose?: () => void;
  onBind?: (userFullInfo: UserFullInfo) => void;
  onRecharge?: (userFullInfo: UserFullInfo) => void;
  onUnbind?: (userFullInfo: UserFullInfo) => void;
  userId?: number;
  userMobile?: string;
  userName?: string;
  wecomId?: string;
  userFullInfo?: UserFullInfo;
  showCloseBtn?: boolean;
  zIndex?: number;
  /**非 Modal的平铺模式 */
  flatMode?: boolean;
} & CommandEntityReady;

const UserModal: React.FC<UserModalProps> = ({
  onClose,
  onBind,
  onRecharge,
  onUnbind,
  userId,
  wecomId,
  userMobile,
  userName,
  userFullInfo: initUserFullInfo,
  showCloseBtn = false,
  flatMode = false,
  zIndex,
  onCommandEntityReady,
}: UserModalProps) => {
  const { styles } = useStyles();
  const [open, setOpen] = useState(false);
  const [findedUserId, setFindedUserId] = useState<number>();

  // 根据手机号查找用户
  const {
    payload: findUserResult,
    send: findUsers,
    loading: findUsersLoading,
    error: findUsersError,
  } = useRequest<{
    list: User[];
    page: Page;
  }>(
    {
      method: 'get',
      url: `${UserEndpoint}`,
    },
    {
      manual: true,
    }
  );

  // 获取用户完整信息
  const {
    payload: userFullInfo,
    reset: resetUserFullInfo,
    send: getUserInfo,
    setPayload: updateUserFullInfo,
    loading,
    error,
  } = useUserFullInfo();

  useEffect(() => {
    // 存在用户信息、或输入了手机号
    if (userFullInfo || initUserFullInfo || userMobile) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [userFullInfo, initUserFullInfo, userMobile]);

  useEffect(() => {
    // 有任意参数，查询详细信息
    if (userId || wecomId || userName || findedUserId) {
      setOpen(true);
      getUserInfo({
        userId: userId || findedUserId,
        userName,
        wecomId,
      }).then(data => {
        if (!data) return;
        onCommandEntityReady?.({
          type: CommandEntityTypeEnum.USER,
          key: data.user.username,
          title: data.user.remarkName || data.user.nickname || '匿名客户',
          img: data.user.avatar,
          subTitle: `${data.user.mobile}`,
          props: {
            userName: data.user.username,
          },
        });
      });
    }
  }, [userId, userName, wecomId, findedUserId]);

  useEffect(() => {
    // 只输入了手机号， 模糊匹配
    if (userMobile && userMobile.length && !userId && !userName && !wecomId) {
      findUsers({
        params: { mobile: userMobile },
      }).then(result => {
        if (result.list && result.list.length === 1) {
          // 如果只查到一个用户，直接选中
          setFindedUserId(result.list[0].id);
        }
      });
    }
  }, [userMobile, userId, wecomId, userName]);

  const afterClose = () => {
    resetUserFullInfo();
    // setUserMobile(null);
    // setUserId(null);
    // setUserName(null);
    // setWecomId(null);
    setFindedUserId(null);
    onClose?.();
  };

  const hasMultiUser = userMobile && !userId && !userName && !findedUserId && !wecomId;
  const info = userFullInfo || initUserFullInfo;
  const userInfoCom = hasMultiUser ? null : (
    <UserInfo
      updateUserFullInfo={updateUserFullInfo}
      loading={loading}
      userInfo={info}
      // showOperations={!!(onBind && onUnbind)}
      showOperations={IS_POS}
      showCloseBtn={showCloseBtn}
      onRequestClose={() => {
        setOpen(false);
      }}
      onRequestBind={info => {
        setOpen(false);
        onBind?.(info);
      }}
      onRequestRecharge={info => {
        setOpen(false);
        onRecharge?.(info);
      }}
      onRequestUnbind={info => {
        setOpen(false);
        onUnbind?.(info);
      }}
      onRequestReload={() => {
        getUserInfo({
          userId: info.user.id,
        }).then(payload => {
          message.destroy();
          message.success('用户数据更新成功');
          emitGlobalEvent({ type: 'actionUserUpdate', payload });
        });
      }}
    />
  );

  if (flatMode) {
    return userInfoCom;
  }

  return (
    <Modal
      zIndex={zIndex}
      className={styles.modal}
      closable={false}
      destroyOnHidden
      open={open}
      afterClose={afterClose}
      okText={'保存'}
      width={hasMultiUser ? 400 : 520}
      footer={null}
      styles={{ content: { padding: 0 } }}
      onCancel={() => {
        setOpen(false);
      }}>
      <div className={styles.wrapper}>
        {/*根据手机号查找结果*/}
        {hasMultiUser && (
          <AsyncWrapper loading={findUsersLoading} error={findUsersError} render={!!findUserResult}>
            {() => {
              return (
                <MultiUserSelector
                  onSelect={user => {
                    setFindedUserId(user.id);
                  }}
                  queryMobile={userMobile}
                  userList={findUserResult.list}
                />
              );
            }}
          </AsyncWrapper>
        )}
        {userInfoCom}
        <div className={styles.actions}>
          <CloseCircleOutlined
            className={styles.actionItem}
            onClick={() => {
              playSound('click');
              setOpen(false);
            }}
          />
        </div>
      </div>
    </Modal>
  );
};

export default UserModal;
